import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography, Button } from "@mui/material";
import { ApplicationsService } from "gen/clients";
import { useQuery } from "react-query";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { SelectOption } from "@mui/base";
import { LoadingButton } from "components/LoadingButton/LoadingButton";

interface Props {
  applicationId: string;
  currentPresetId?: string;
  onClose: () => void;
  onSave: (presetId: string) => void;
}

interface FormValues {
  presetId: string;
}

const EditApplicationPresetDialog: React.FC<Props> = ({ applicationId, currentPresetId, onClose, onSave }) => {
  const { data: application, isLoading } = useQuery(["getApplication", applicationId], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationId }),
    enabled: !!applicationId,
    cacheTime: 0
  });

  const presetOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!application?.settingPresets) {
      return [];
    }
    return application.settingPresets.map(p => ({ label: p.name, value: p.id }));
  }, [application]);

  const validationSchema = Yup.object().shape({
    presetId: Yup.string().required("Preset is required")
  });

  const initialValues: FormValues = {
    presetId: currentPresetId || ""
  };

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      onSave(values.presetId);
    },
    [onSave]
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Machine Translation Preset</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Select the configuration preset for the Machine Translation application.
                </Typography>
                <SelectOneField
                  name="presetId"
                  label="Application Preset"
                  options={presetOptions}
                  required
                  isLoading={isLoading}
                  disabled={isLoading}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                isLoading={isSubmitting || isLoading}
                disabled={!isValid || !dirty}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditApplicationPresetDialog };
